import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getScrollParent } from '@/core/utils/scrollToStartIfNeeded';
import { DockAnchor, DockedCopy } from './styles';

/** `start` — левый угол (кнопка «Назад»), `center` — между ним и крестиком. */
export type ModalStickySlot = 'start' | 'center';

/** `onScroll` — элемент в потоке, в шапку попадает, только когда при прокрутке уходит под неё. */
export type ModalStickyMode = 'always' | 'onScroll';

export type ModalStickySlots = Record<ModalStickySlot, HTMLElement | null>;

/** null — вне модалки или у модалки нет шапки (`unclosed`). */
export const ModalStickyBarContext = createContext<ModalStickySlots | null>(null);

interface ModalStickyBarSlotProps {
	slot: ModalStickySlot;
	mode?: ModalStickyMode;
	children: React.ReactNode;
}

interface DockOnScrollProps {
	node: HTMLElement;
	children: React.ReactNode;
}

/** Оригинал прячется через visibility — место в потоке остаётся; копия aria-hidden — для скринридера элемент один. */
const DockOnScroll: React.FC<DockOnScrollProps> = ({ node, children }) => {
	const anchorRef = useRef<HTMLDivElement>(null);
	const [docked, setDocked] = useState(false);

	useEffect(() => {
		const anchor = anchorRef.current;
		const bar = node.parentElement;
		if (!anchor || !bar) {
			return undefined;
		}
		const scroller: HTMLElement | Window = (getScrollParent(anchor) as HTMLElement | null) ?? window;
		const update = () => {
			setDocked(anchor.getBoundingClientRect().bottom <= bar.getBoundingClientRect().bottom);
		};
		update();
		scroller.addEventListener('scroll', update, { passive: true });
		window.addEventListener('resize', update);
		return () => {
			scroller.removeEventListener('scroll', update);
			window.removeEventListener('resize', update);
		};
	}, [node]);

	return (
		<>
			<DockAnchor ref={anchorRef} $hidden={docked}>
				{children}
			</DockAnchor>
			{docked && createPortal(<DockedCopy aria-hidden='true'>{children}</DockedCopy>, node)}
		</>
	);
};

/** Вне модалки и в модалке без шапки рендерит children на месте. */
const ModalStickyBarSlot: React.FC<ModalStickyBarSlotProps> = ({ slot, mode = 'always', children }) => {
	const slots = useContext(ModalStickyBarContext);
	const node = slots?.[slot] ?? null;

	if (mode === 'onScroll') {
		return node ? <DockOnScroll node={node}>{children}</DockOnScroll> : <>{children}</>;
	}
	if (!slots) {
		return <>{children}</>;
	}
	return node ? createPortal(children, node) : null;
};

export default ModalStickyBarSlot;
